import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Upload, FileText, Download, Shield,
  CheckCircle, XCircle, Loader, AlertTriangle, Archive, Share2
} from 'lucide-react';
import clientPortalService from '../../services/clientPortalService';
import api from '../../services/api';

const PortalDocuments = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedCase = searchParams.get('case_id');
  const fileInputRef = useRef(null);

  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(preselectedCase || '');
  const [documents, setDocuments] = useState([]);
  const [sharedDocuments, setSharedDocuments] = useState([]);
  const [courtRecords, setCourtRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [description, setDescription] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [activeTab, setActiveTab] = useState('shared'); // 'shared', 'uploads', or 'request'
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [requesting, setRequesting] = useState(null);
  const [requestMessage, setRequestMessage] = useState('');

  useEffect(() => {
    loadCases();
    loadCourtRecords();
  }, []);

  useEffect(() => {
    if (selectedCase) {
      loadDocuments();
      loadSharedDocuments();
      loadAvailableDocuments();
    }
  }, [selectedCase]);

  const loadCases = async () => {
    try {
      const data = await clientPortalService.getCases();
      setCases(data);
      if (!selectedCase && data.length > 0) {
        setSelectedCase(data[0].case_id);
      }
    } catch (err) {
      console.error('Failed to load cases:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    if (!selectedCase) return;
    try {
      const data = await clientPortalService.getCaseDocuments(selectedCase);
      setDocuments(data);
    } catch (err) {
      console.error('Failed to load documents:', err);
    }
  };

  const loadCourtRecords = async () => {
    try {
      setCourtRecords(await clientPortalService.getCourtRecords());
    } catch (e) { /* fail-closed: show nothing */ }
  };

  const loadSharedDocuments = async () => {
    try {
      // Get auth token from localStorage
      const token = localStorage.getItem('client_portal_token');
      const response = await api.get('/client-portal/documents/shared', {
        headers: { Authorization: `Bearer ${token}` },
        params: { case_id: selectedCase }
      });
      setSharedDocuments(response.data);
    } catch (err) {
      console.error('Failed to load shared documents:', err);
    }
  };

  const loadAvailableDocuments = async () => {
    try {
      const token = localStorage.getItem('client_portal_token');
      const response = await api.get('/client-portal/documents/available', {
        headers: { Authorization: `Bearer ${token}` },
        params: { case_id: selectedCase }
      });
      setAvailableDocuments(response.data);
    } catch (err) {
      console.error('Failed to load available documents:', err);
    }
  };

  const handleRequestDocument = async (doc) => {
    try {
      setRequesting(doc.document_id);
      const token = localStorage.getItem('client_portal_token');

      const response = await api.post(
        '/client-portal/documents/request',
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            document_id: doc.document_id,
            message: requestMessage || null
          }
        }
      );

      // Reload available documents to update status
      loadAvailableDocuments();
      setRequestMessage('');

      if (response.data.status === 'pending') {
        alert('Your request has been submitted. An attorney will review it shortly.');
      } else if (response.data.status === 'already_approved') {
        alert('You already have access to this document. Check the "Shared with You" tab.');
        loadSharedDocuments();
      } else if (response.data.status === 'already_pending') {
        alert('Your request is already pending approval.');
      }
    } catch (err) {
      console.error('Request failed:', err);
      alert(err.response?.data?.detail || 'Failed to request document access.');
    } finally {
      setRequesting(null);
    }
  };

  const handleDownload = async (doc) => {
    // iOS Safari treats <a download> on a fetched blob as a silent no-op, so the
    // request 200s with the full file but nothing saves. Open a tab synchronously
    // inside the click gesture, then point it at the blob (Safari renders it; the
    // client saves via the Share sheet). The non-iOS path below is byte-identical,
    // so this can only fail closed — desktop/Android cannot regress.
    const isIOS = /iP(hone|ad|od)/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const iosWin = isIOS ? window.open('', '_blank') : null;
    try {
      setDownloading(doc.document_id);
      const token = localStorage.getItem('client_portal_token');

      const response = await api.get(
        `/client-portal/documents/${doc.document_id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      if (isIOS) {
        const iosUrl = window.URL.createObjectURL(
          new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' }));
        if (iosWin) { iosWin.location = iosUrl; }
        else { alert('Please allow pop-ups for this site to open documents, then tap download again.'); }
        setTimeout(() => window.URL.revokeObjectURL(iosUrl), 60000);
        return;
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (iosWin) iosWin.close();
      console.error('Download failed:', err);
      alert('Download failed. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadAll = async () => {
    if (sharedDocuments.length === 0) return;

    // iOS Safari: see handleDownload — open a tab in-gesture; non-iOS byte-identical.
    const isIOS = /iP(hone|ad|od)/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const iosWin = isIOS ? window.open('', '_blank') : null;
    try {
      setDownloadingAll(true);
      const token = localStorage.getItem('client_portal_token');

      const response = await api.post(
        '/client-portal/documents/download-zip',
        { document_ids: sharedDocuments.map(d => d.document_id) },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      if (isIOS) {
        const iosUrl = window.URL.createObjectURL(
          new Blob([response.data], { type: response.headers['content-type'] || 'application/zip' }));
        if (iosWin) { iosWin.location = iosUrl; }
        else { alert('Please allow pop-ups for this site to download, then tap Download All again.'); }
        setTimeout(() => window.URL.revokeObjectURL(iosUrl), 60000);
        return;
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'documents.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (iosWin) iosWin.close();
      console.error('ZIP download failed:', err);
      alert('ZIP download failed. Please try again.');
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleFileSelect = async (files) => {
    if (!selectedCase || !files || files.length === 0) return;

    for (const file of files) {
      await uploadFile(file);
    }
  };

  const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

  const uploadFile = async (file) => {
    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadProgress({
        filename: file.name,
        status: 'error',
        message: `File too large (${formatFileSize(file.size)}). Maximum size is 100MB.`
      });
      return;
    }
    try {
      setUploading(true);
      setUploadProgress({
        filename: file.name,
        status: 'uploading',
        message: 'Uploading and scanning for viruses...'
      });

      const result = await clientPortalService.uploadDocument(
        selectedCase,
        file,
        description
      );

      setUploadProgress({
        filename: file.name,
        status: result.status === 'processed' ? 'success' : 'error',
        message: result.message
      });

      // Reload documents
      loadDocuments();
      setDescription('');

      // Clear progress after 3 seconds
      setTimeout(() => setUploadProgress(null), 3000);

    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Upload failed';
      setUploadProgress({
        filename: file.name,
        status: 'error',
        message: errorMessage
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(Array.from(e.dataTransfer.files));
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return '🖼️';
    if (mimeType?.includes('pdf')) return '📄';
    if (mimeType?.includes('word') || mimeType?.includes('document')) return '📝';
    if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet')) return '📊';
    return '📁';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900">
      {/* Header */}
      <header className="bg-virgo-navy-800/80 border-b border-virgo-teal-700/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center space-x-4">
          <button
            onClick={() => navigate('/portal/dashboard')}
            className="text-virgo-teal-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <div className="flex items-center space-x-3">
            <Upload className="text-cyan-400" size={24} />
            <h1 className="text-xl font-bold text-white">Documents</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Case Selector */}
        <div className="mb-6">
          <label className="block text-sm text-virgo-teal-400 mb-2">Select Case</label>
          <select
            value={selectedCase}
            onChange={(e) => setSelectedCase(e.target.value)}
            className="w-full max-w-full px-4 py-2 bg-virgo-navy-700 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
          >
            <option value="">Choose a case...</option>
            {cases.map(c => (
              <option key={c.case_id} value={c.case_id}>{c.case_name}</option>
            ))}
          </select>
        </div>

        {selectedCase && (
          <>
            {/* Tabs */}
            <div className="flex space-x-4 mb-6 border-b border-virgo-teal-700/30 overflow-x-auto">
              <button
                onClick={() => setActiveTab('shared')}
                className={`px-4 py-2 font-medium flex items-center space-x-2 flex-shrink-0 whitespace-nowrap ${
                  activeTab === 'shared'
                    ? 'text-cyan-400 border-b-2 border-cyan-400'
                    : 'text-virgo-teal-400 hover:text-white'
                }`}
              >
                <Share2 size={18} />
                <span>Shared with You</span>
                {sharedDocuments.length > 0 && (
                  <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-300 rounded text-xs">
                    {sharedDocuments.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('request')}
                className={`px-4 py-2 font-medium flex items-center space-x-2 flex-shrink-0 whitespace-nowrap ${
                  activeTab === 'request'
                    ? 'text-cyan-400 border-b-2 border-cyan-400'
                    : 'text-virgo-teal-400 hover:text-white'
                }`}
              >
                <FileText size={18} />
                <span>Request Access</span>
                {availableDocuments.filter(d => !d.share_status).length > 0 && (
                  <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 rounded text-xs">
                    {availableDocuments.filter(d => !d.share_status).length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('uploads')}
                className={`px-4 py-2 font-medium flex items-center space-x-2 flex-shrink-0 whitespace-nowrap ${
                  activeTab === 'uploads'
                    ? 'text-cyan-400 border-b-2 border-cyan-400'
                    : 'text-virgo-teal-400 hover:text-white'
                }`}
              >
                <Upload size={18} />
                <span>Your Uploads</span>
                {documents.length > 0 && (
                  <span className="px-2 py-0.5 bg-virgo-teal-500/20 text-virgo-teal-300 rounded text-xs">
                    {documents.length}
                  </span>
                )}
              </button>
            </div>

            {/* Shared Documents Tab */}
            {activeTab === 'shared' && courtRecords.length > 0 && (
              <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-cyan-700/40 mb-4">
                <div className="p-4 border-b border-virgo-navy-600">
                  <h2 className="font-semibold text-white">Court records</h2>
                  <p className="text-xs text-cyan-400 mt-0.5">Filed in your case — shown automatically</p>
                </div>
                <div className="divide-y divide-virgo-navy-600">
                  {courtRecords.map((r) => (
                    <div key={r.document_id} className="p-4 flex items-center justify-between gap-3 min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="text-cyan-500 shrink-0" size={18} />
                        <p className="text-white text-sm truncate">{r.filename}</p>
                      </div>
                      <button
                        onClick={() => handleDownload({ document_id: r.document_id, filename: r.filename })}
                        disabled={downloading === r.document_id}
                        className="flex items-center gap-1 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded text-sm shrink-0 disabled:opacity-50">
                        {downloading === r.document_id ? <Loader className="animate-spin" size={14} /> : <Download size={14} />}
                        <span>Download</span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'shared' && (
              <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30">
                <div className="p-4 border-b border-virgo-navy-600 flex items-center justify-between">
                  <h2 className="font-semibold text-white">Shared with you</h2>
                  {sharedDocuments.length > 1 && (
                    <button
                      onClick={handleDownloadAll}
                      disabled={downloadingAll}
                      className="flex items-center space-x-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-colors disabled:opacity-50"
                    >
                      {downloadingAll ? (
                        <Loader className="animate-spin" size={16} />
                      ) : (
                        <Archive size={16} />
                      )}
                      <span>{downloadingAll ? 'Creating ZIP...' : 'Download All'}</span>
                    </button>
                  )}
                </div>

                {sharedDocuments.length === 0 ? (
                  <div className="p-12 text-center">
                    <FileText className="text-virgo-teal-500 mx-auto mb-4" size={48} />
                    <p className="text-virgo-teal-400">No documents have been shared with you yet</p>
                    <p className="text-sm text-virgo-teal-500 mt-2">
                      When your legal team shares documents, they'll appear here
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-virgo-navy-600">
                    {sharedDocuments.map((doc) => (
                      <div
                        key={doc.document_id}
                        className="p-4 flex items-center justify-between gap-3 min-w-0 hover:bg-virgo-navy-800/50 transition-colors"
                      >
                        <div className="flex items-center space-x-4 min-w-0 flex-1">
                          <span className="text-2xl flex-shrink-0">{getFileIcon(doc.mime_type)}</span>
                          <div className="min-w-0">
                            <p className="text-white font-medium truncate">{doc.filename}</p>
                            <div className="flex items-center space-x-3 text-sm text-virgo-teal-500">
                              <span>{formatFileSize(doc.file_size)}</span>
                              <span>·</span>
                              <span>Shared {new Date(doc.shared_at).toLocaleDateString()}</span>
                              {doc.category === 'Signed Documents' && (
                                <>
                                  <span>·</span>
                                  <span className="px-2 py-0.5 bg-green-500/20 text-green-300 rounded text-xs flex items-center space-x-1">
                                    <CheckCircle size={12} />
                                    <span>Signed</span>
                                  </span>
                                </>
                              )}
                              {doc.expires_at && (
                                <>
                                  <span>·</span>
                                  <span className="text-amber-400">
                                    Expires {new Date(doc.expires_at).toLocaleDateString()}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        <button
                          onClick={() => handleDownload(doc)}
                          disabled={downloading === doc.document_id}
                          className="flex items-center space-x-2 px-4 py-2 bg-virgo-navy-600 hover:bg-virgo-navy-500 text-white rounded-lg transition-colors disabled:opacity-50"
                        >
                          {downloading === doc.document_id ? (
                            <Loader className="animate-spin" size={16} />
                          ) : (
                            <Download size={16} />
                          )}
                          <span>{downloading === doc.document_id ? 'Downloading...' : 'Download'}</span>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Request Access Tab */}
            {activeTab === 'request' && (
              <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30">
                <div className="p-4 border-b border-virgo-navy-600">
                  <h2 className="font-semibold text-white">Request Document Access</h2>
                  <p className="text-sm text-virgo-teal-400 mt-1">
                    Select documents you need access to. An attorney will review your request.
                  </p>
                </div>

                {availableDocuments.length === 0 ? (
                  <div className="p-12 text-center">
                    <FileText className="text-virgo-teal-500 mx-auto mb-4" size={48} />
                    <p className="text-virgo-teal-400">No documents available to request</p>
                  </div>
                ) : (
                  <div className="divide-y divide-virgo-navy-600">
                    {availableDocuments.map((doc) => (
                      <div
                        key={doc.document_id}
                        className="p-4 flex items-center justify-between gap-3 min-w-0 hover:bg-virgo-navy-800/50 transition-colors"
                      >
                        <div className="flex items-center space-x-4 min-w-0 flex-1">
                          <span className="text-2xl flex-shrink-0">{getFileIcon(doc.mime_type)}</span>
                          <div className="min-w-0">
                            <p className="text-white font-medium truncate">{doc.filename}</p>
                            <div className="flex items-center space-x-3 text-sm text-virgo-teal-500">
                              <span>{formatFileSize(doc.file_size)}</span>
                              <span>·</span>
                              <span>{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                              {doc.share_status === 'pending' && (
                                <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 rounded text-xs">
                                  Pending Approval
                                </span>
                              )}
                              {doc.share_status === 'approved' && (
                                <span className="px-2 py-0.5 bg-green-500/20 text-green-300 rounded text-xs">
                                  Approved
                                </span>
                              )}
                              {doc.share_status === 'rejected' && (
                                <span className="px-2 py-0.5 bg-red-500/20 text-red-300 rounded text-xs">
                                  Rejected
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {!doc.share_status && (
                          <button
                            onClick={() => handleRequestDocument(doc)}
                            disabled={requesting === doc.document_id}
                            className="flex items-center space-x-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-colors disabled:opacity-50"
                          >
                            {requesting === doc.document_id ? (
                              <Loader className="animate-spin" size={16} />
                            ) : (
                              <FileText size={16} />
                            )}
                            <span>{requesting === doc.document_id ? 'Requesting...' : 'Request Access'}</span>
                          </button>
                        )}
                        {doc.share_status === 'approved' && (
                          <span className="text-sm text-green-400">
                            Available in "Shared with You" tab
                          </span>
                        )}
                        {doc.share_status === 'pending' && (
                          <span className="text-sm text-amber-400">
                            Awaiting attorney approval
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Uploads Tab */}
            {activeTab === 'uploads' && (
              <>
                {/* Upload Area */}
                <div className="mb-8">
                  <div
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                      dragActive
                        ? 'border-cyan-500 bg-cyan-500/10'
                        : 'border-virgo-teal-700/50 bg-virgo-navy-800/30'
                    }`}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={(e) => handleFileSelect(Array.from(e.target.files))}
                      className="hidden"
                    />

                    <Upload className="mx-auto mb-4 text-virgo-teal-400" size={48} />
                    <h3 className="text-lg font-semibold text-white mb-2">
                      Drag and drop files here
                    </h3>
                    <p className="text-virgo-teal-400 mb-4">or</p>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 transition-all"
                    >
                      Browse Files
                    </button>

                    <div className="mt-4 flex items-center justify-center text-sm text-virgo-teal-500">
                      <Shield size={14} className="mr-1" />
                      <span>All files are scanned for viruses before processing</span>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="mt-4">
                    <label className="block text-sm text-virgo-teal-400 mb-2">
                      Description (optional)
                    </label>
                    <input
                      type="text"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="What is this document for?"
                      className="w-full px-4 py-2 bg-virgo-navy-700 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>

                  {/* Upload Progress */}
                  {uploadProgress && (
                    <div className={`mt-4 p-4 rounded-lg border ${
                      uploadProgress.status === 'success'
                        ? 'bg-green-500/10 border-green-500/30'
                        : uploadProgress.status === 'error'
                        ? 'bg-red-500/10 border-red-500/30'
                        : 'bg-blue-500/10 border-blue-500/30'
                    }`}>
                      <div className="flex items-center space-x-3">
                        {uploadProgress.status === 'uploading' && (
                          <Loader className="animate-spin text-blue-400" size={20} />
                        )}
                        {uploadProgress.status === 'success' && (
                          <CheckCircle className="text-green-400" size={20} />
                        )}
                        {uploadProgress.status === 'error' && (
                          <XCircle className="text-red-400" size={20} />
                        )}
                        <div>
                          <p className="text-white font-medium">{uploadProgress.filename}</p>
                          <p className={`text-sm ${
                            uploadProgress.status === 'success' ? 'text-green-400' :
                            uploadProgress.status === 'error' ? 'text-red-400' : 'text-blue-400'
                          }`}>
                            {uploadProgress.message}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Uploaded Documents List */}
                <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30">
                  <div className="p-4 border-b border-virgo-navy-600 flex items-center justify-between">
                    <h2 className="font-semibold text-white">Your Uploaded Documents</h2>
                    <span className="text-sm text-virgo-teal-400">{documents.length} files</span>
                  </div>

                  {documents.length === 0 ? (
                    <div className="p-12 text-center">
                      <FileText className="text-virgo-teal-500 mx-auto mb-4" size={48} />
                      <p className="text-virgo-teal-400">No documents uploaded yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-virgo-navy-600">
                      {documents.map((doc) => (
                        <div
                          key={doc.document_id}
                          className="p-4 flex items-center justify-between gap-3 min-w-0 hover:bg-virgo-navy-800/50 transition-colors"
                        >
                          <div className="flex items-center space-x-4">
                            <span className="text-2xl flex-shrink-0">{getFileIcon(doc.mime_type)}</span>
                            <div>
                              <p className="text-white font-medium truncate">{doc.filename}</p>
                              <div className="flex items-center space-x-3 text-sm text-virgo-teal-500">
                                <span>{formatFileSize(doc.file_size)}</span>
                                <span>·</span>
                                <span>{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                                <span className="px-2 py-0.5 bg-green-500/20 text-green-300 rounded text-xs">
                                  Uploaded by you
                                </span>
                              </div>
                              {doc.description && (
                                <p className="text-sm text-virgo-teal-400 mt-1">{doc.description}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Security Notice */}
            <div className="mt-6 p-4 bg-virgo-navy-800/50 rounded-lg border border-virgo-teal-700/30">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="text-amber-400 flex-shrink-0 mt-0.5" size={20} />
                <div className="text-sm text-virgo-teal-400">
                  <p className="font-medium text-white mb-1">Document Security</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>All uploaded files are automatically scanned for viruses</li>
                    <li>Files are encrypted and stored securely</li>
                    <li>Maximum file size: 100MB (videos accepted)</li>
                    <li>Infected files will be rejected</li>
                  </ul>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default PortalDocuments;
